import { FC, useRef, useState } from "react";
import { Formik, Form, FormikConfig } from "formik";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import {
    UpdateOrganizationClientPayload,
    useUpdateOrganizationClientMutation,
} from "@api/organizationClient.api";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useAutofocus } from "@hooks/useAutofocus";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import actionsStyles from "../ClientActions.module.scss";
import { ClientDetailsRow } from "../views/clientViewConfig";
import { EditClientFormSchema, editClientSchema, isFlatRatePlan } from "./lib";
import { EditClientFormFields } from "./EditClientFormFields";
import styles from "./EditClient.module.scss";

interface EditClientModalProps {
    client: ClientDetailsRow;
    onClose: () => void;
}

export const EditClientModal: FC<EditClientModalProps> = ({
    client,
    onClose,
}) => {
    const [loading, setLoading] = useState(false);
    const activeOrganization = useActiveOrganization();
    const { toast } = useToaster();
    const updateOrganizationClientMutation =
        useUpdateOrganizationClientMutation();
    const inputRef = useRef<HTMLInputElement>(null);
    const billingOwnedByActiveOrganization = client.isOrganizationBillingOwner;

    useAutofocus(inputRef);

    const form: FormikConfig<EditClientFormSchema> = {
        initialValues: {
            clientName: client.name,
            bookkeepingStartDate: client.bookkeepingStartDate,
            servicesOffered: client.services ?? [],
            note: client.note ?? null,
            ...(billingOwnedByActiveOrganization ? { plan: client.plan } : {}),
        },
        validate: validateFormikWithZodSchema(editClientSchema),
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async (values) => {
                if (!activeOrganization) {
                    return;
                }

                const payload: UpdateOrganizationClientPayload = {
                    clientName: values.clientName,
                    bookkeepingStartDate: values.bookkeepingStartDate,
                    servicesOffered: values.servicesOffered,
                    note: values.note ?? null,
                };

                const plan = values.plan ?? null;
                if (billingOwnedByActiveOrganization && isFlatRatePlan(plan)) {
                    payload.plan = plan;
                }

                await updateOrganizationClientMutation.mutateAsync({
                    organizationId: activeOrganization.id,
                    clientId: client.id,
                    payload,
                });

                toast("Client updated");
                onClose();
            },
        }),
    };

    return (
        <>
            <StandardModalHeader className={actionsStyles.modalHeader}>
                Edit client
            </StandardModalHeader>
            <StandardModalBody className={actionsStyles.modalBody}>
                <Formik {...form}>
                    {({ isValid }) => (
                        <Form>
                            <section className={styles.content}>
                                <EditClientFormFields
                                    focusInputRef={inputRef}
                                    billingOwnedByActiveOrganization={
                                        billingOwnedByActiveOrganization
                                    }
                                    numberOfBusinessEntities={
                                        client.entities.filter(
                                            ({ isBusiness }) => isBusiness,
                                        ).length
                                    }
                                    isFirmWorkspace={client.isFirmWorkspace}
                                />
                            </section>

                            <footer className={styles.footer}>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={onClose}
                                    disabled={loading}
                                    size="xl"
                                >
                                    Cancel
                                </Button>
                                <ButtonWithLoader
                                    type="submit"
                                    variant="primary"
                                    disabled={!isValid || loading}
                                    loading={loading}
                                    size="xl"
                                >
                                    Save
                                </ButtonWithLoader>
                            </footer>
                        </Form>
                    )}
                </Formik>
            </StandardModalBody>
        </>
    );
};
